import React, { useState } from 'react';
import { 
  Heart, FileText, Calendar, User, Clock, 
  CheckCircle, XCircle, Eye, Edit3, Plus, Search,
  AlertCircle, Shield, TrendingUp, TrendingDown,
  Download, Printer, Send, Flag, Bell, Users,
  Camera, Paperclip, MapPin, Activity, Zap,
  Stethoscope, Pill, Bed, Thermometer, Activity as ActivityIcon,
  Brain, Eye as EyeIcon, Hand, Foot, Lungs
} from 'lucide-react';

const PalliativeCareTemplates = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedResident, setSelectedResident] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Mock palliative care data
  const palliativeTemplates = [
    {
      id: 'PCT-001',
      name: 'End-of-Life Care Plan',
      category: 'Comfort Care',
      description: 'Comprehensive end-of-life care focusing on comfort, dignity, and family support',
      status: 'Active',
      lastUsed: '2024-07-20',
      usageCount: 15,
      components: [
        'Pain Management Protocol',
        'Symptom Control Guidelines',
        'Family Communication Plan',
        'Spiritual Care Assessment',
        'Advance Care Directives',
        'Bereavement Support'
      ],
      residents: ['Alice Smith', 'Bob Johnson'],
      createdBy: 'Dr. Sarah Wilson',
      createdDate: '2024-01-15'
    },
    {
      id: 'PCT-002',
      name: 'Pain Management Protocol',
      category: 'Symptom Management',
      description: 'Evidence-based pain assessment and management for palliative patients',
      status: 'Active',
      lastUsed: '2024-07-19',
      usageCount: 23,
      components: [
        'Pain Assessment Tools',
        'Medication Guidelines',
        'Non-pharmacological Interventions',
        'Side Effect Management',
        'Dosage Adjustment Protocols'
      ],
      residents: ['Charlie Brown', 'Diana Prince', 'Eve Adams'],
      createdBy: 'Nurse Jennifer',
      createdDate: '2024-02-01'
    },
    {
      id: 'PCT-003',
      name: 'Respiratory Distress Management',
      category: 'Symptom Management',
      description: 'Protocol for managing respiratory symptoms in palliative care',
      status: 'Active',
      lastUsed: '2024-07-18',
      usageCount: 8,
      components: [
        'Breathing Assessment',
        'Oxygen Therapy Guidelines',
        'Positioning Techniques',
        'Medication Protocols',
        'Family Education'
      ],
      residents: ['Alice Smith'],
      createdBy: 'Dr. Michael Chen',
      createdDate: '2024-01-20'
    },
    {
      id: 'PCT-004',
      name: 'Family Support Package',
      category: 'Psychosocial Care',
      description: 'Comprehensive family support and communication framework',
      status: 'Active',
      lastUsed: '2024-07-17',
      usageCount: 12,
      components: [
        'Communication Guidelines',
        'Grief Counseling Resources',
        'Practical Support Services',
        'Legal and Financial Guidance',
        'Spiritual Care Resources'
      ],
      residents: ['Bob Johnson', 'Charlie Brown'],
      createdBy: 'Social Worker Lisa',
      createdDate: '2024-01-25'
    },
    {
      id: 'PCT-005',
      name: 'Nutrition Support Plan',
      category: 'Comfort Care',
      description: 'Nutritional support and feeding guidelines for palliative patients',
      status: 'Draft',
      lastUsed: '2024-07-10',
      usageCount: 5,
      components: [
        'Nutritional Assessment',
        'Feeding Guidelines',
        'Hydration Management',
        'Family Education',
        'Comfort Measures'
      ],
      residents: ['Diana Prince'],
      createdBy: 'Dietitian Mary',
      createdDate: '2024-03-01'
    }
  ];

  const residents = [
    { id: 'RES001', name: 'Alice Smith', room: 'A101', age: 78, condition: 'Advanced Cancer', prognosis: 'Weeks' },
    { id: 'RES002', name: 'Bob Johnson', room: 'A102', age: 82, condition: 'End-stage Dementia', prognosis: 'Months' },
    { id: 'RES003', name: 'Charlie Brown', room: 'B201', age: 75, condition: 'Heart Failure', prognosis: 'Months' },
    { id: 'RES004', name: 'Diana Prince', room: 'B202', age: 85, condition: 'Advanced COPD', prognosis: 'Weeks' },
    { id: 'RES005', name: 'Eve Adams', room: 'C301', age: 79, condition: 'Motor Neuron Disease', prognosis: 'Months' }
  ];

  const filteredTemplates = palliativeTemplates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || template.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const showNotification = (message, type) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'text-green-600 bg-green-100';
      case 'Draft': return 'text-yellow-600 bg-yellow-100';
      case 'Archived': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Comfort Care': return <Heart className="w-5 h-5 text-pink-600" />;
      case 'Symptom Management': return <Stethoscope className="w-5 h-5 text-blue-600" />;
      case 'Psychosocial Care': return <Users className="w-5 h-5 text-purple-600" />;
      default: return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const handleNewTemplate = () => {
    setShowNewTemplate(true);
    showNotification('Creating new palliative care template...', 'info');
  };

  const handleViewTemplate = (template) => {
    setSelectedTemplate(template);
    showNotification(`Viewing template ${template.name}`, 'success');
  };

  const handleEditTemplate = (template) => {
    console.log('Editing template:', template.id);
    showNotification(`Editing template ${template.name}`, 'info');
  };

  const handleApplyTemplate = (template, resident) => {
    console.log('Applying template:', template.id, 'to resident:', resident.id);
    showNotification(`Template ${template.name} applied to ${resident.name}`, 'success');
  };

  const handleDeleteTemplate = (template) => {
    console.log('Deleting template:', template.id);
    showNotification(`Template ${template.name} deleted`, 'success');
  };

  const handleExportTemplate = (template) => {
    console.log('Exporting template:', template.id);
    showNotification(`Template ${template.name} exported`, 'success');
  };

  const handlePrintTemplate = (template) => {
    console.log('Printing template:', template.id);
    showNotification(`Template ${template.name} printed`, 'info');
  };

  const stats = {
    total: palliativeTemplates.length,
    active: palliativeTemplates.filter(t => t.status === 'Active').length,
    draft: palliativeTemplates.filter(t => t.status === 'Draft').length,
    totalUsage: palliativeTemplates.reduce((sum, t) => sum + t.usageCount, 0),
    residents: residents.length,
    comfortCare: palliativeTemplates.filter(t => t.category === 'Comfort Care').length,
    symptomManagement: palliativeTemplates.filter(t => t.category === 'Symptom Management').length,
    psychosocialCare: palliativeTemplates.filter(t => t.category === 'Psychosocial Care').length
  };

  if (!onClose) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 max-w-7xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Notifications */}
        <div className="fixed top-4 right-4 z-[100]">
          {notifications.map(notification => (
            <div
              key={notification.id}
              className={`p-4 mb-2 rounded-lg shadow-md text-white flex items-center gap-3 ${
                notification.type === 'success' ? 'bg-green-500' :
                notification.type === 'error' ? 'bg-red-500' : 'bg-blue-500'
              }`}
            >
              {notification.type === 'success' && <CheckCircle className="w-5 h-5" />}
              {notification.type === 'error' && <XCircle className="w-5 h-5" />}
              {notification.type === 'info' && <FileText className="w-5 h-5" />}
              <span>{notification.message}</span>
            </div>
          ))}
        </div>

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <Heart className="w-7 h-7 text-pink-600" />
            Palliative Care Templates
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { id: 'overview', label: 'Overview', icon: Activity },
            { id: 'templates', label: 'Templates', icon: FileText },
            { id: 'residents', label: 'Residents', icon: Users },
            { id: 'new', label: 'New Template', icon: Plus }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  activeTab === tab.id
                    ? 'bg-pink-600 text-white'
                    : 'bg-pink-100 text-pink-700 hover:bg-pink-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Templates</p>
                    <h3 className="text-2xl font-bold text-gray-900">{stats.total}</h3>
                  </div>
                  <FileText className="w-8 h-8 text-blue-400" />
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Active Templates</p>
                    <h3 className="text-2xl font-bold text-green-600">{stats.active}</h3>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Usage</p>
                    <h3 className="text-2xl font-bold text-purple-600">{stats.totalUsage}</h3>
                  </div>
                  <TrendingUp className="w-8 h-8 text-purple-400" />
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Palliative Residents</p>
                    <h3 className="text-2xl font-bold text-pink-600">{stats.residents}</h3>
                  </div>
                  <Heart className="w-8 h-8 text-pink-400" />
                </div>
              </div>
            </div>

            {/* Category Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-600" />
                  Comfort Care
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Templates</span>
                    <span className="font-semibold">{stats.comfortCare}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-pink-500 h-2 rounded-full" style={{width: `${(stats.comfortCare / stats.total) * 100}%`}}></div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Stethoscope className="w-5 h-5 text-blue-600" />
                  Symptom Management
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Templates</span>
                    <span className="font-semibold">{stats.symptomManagement}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full" style={{width: `${(stats.symptomManagement / stats.total) * 100}%`}}></div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  Psychosocial Care
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Templates</span>
                    <span className="font-semibold">{stats.psychosocialCare}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-500 h-2 rounded-full" style={{width: `${(stats.psychosocialCare / stats.total) * 100}%`}}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Templates */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">Recent Templates</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {palliativeTemplates.slice(0, 3).map((template) => (
                    <div key={template.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        {getCategoryIcon(template.category)}
                        <div>
                          <h4 className="font-medium text-gray-900">{template.name}</h4>
                          <p className="text-sm text-gray-600">{template.category} - Used {template.usageCount} times</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(template.status)}`}>
                          {template.status}
                        </span>
                        <button
                          onClick={() => handleViewTemplate(template)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search templates..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="flex gap-4">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  >
                    <option value="all">All Status</option>
                    <option value="Active">Active</option>
                    <option value="Draft">Draft</option>
                    <option value="Archived">Archived</option>
                  </select>
                  <button
                    onClick={handleNewTemplate}
                    className="px-4 py-3 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    New Template
                  </button>
                </div>
              </div>
            </div>

            {/* Templates Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTemplates.map((template) => (
                <div key={template.id} className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {getCategoryIcon(template.category)}
                        <div>
                          <h3 className="font-semibold text-gray-900">{template.name}</h3>
                          <p className="text-sm text-gray-600">{template.category}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(template.status)}`}>
                        {template.status}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-700 mb-4">{template.description}</p>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Usage Count:</span>
                        <span className="font-medium">{template.usageCount}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Last Used:</span>
                        <span className="font-medium">{template.lastUsed}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Created By:</span>
                        <span className="font-medium">{template.createdBy}</span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {template.components.slice(0, 3).map((component, index) => (
                        <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                          {component}
                        </span>
                      ))}
                      {template.components.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                          +{template.components.length - 3} more
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewTemplate(template)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEditTemplate(template)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Edit Template"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleExportTemplate(template)}
                        className="text-green-600 hover:text-green-900"
                        title="Export Template"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handlePrintTemplate(template)}
                        className="text-gray-600 hover:text-gray-900"
                        title="Print Template"
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Residents Tab */}
        {activeTab === 'residents' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">Palliative Care Residents</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resident</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Condition</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prognosis</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active Templates</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {residents.map((resident) => (
                      <tr key={resident.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-pink-100 flex items-center justify-center">
                                <User className="h-6 w-6 text-pink-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{resident.name}</div>
                              <div className="text-sm text-gray-500">{resident.room} - {resident.age}y</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{resident.condition}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{resident.prognosis}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {palliativeTemplates.filter(t => t.residents.includes(resident.name)).length}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSelectedResident(resident)}
                              className="text-blue-600 hover:text-blue-900"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => console.log('Apply template to', resident.name)}
                              className="text-pink-600 hover:text-pink-900"
                              title="Apply Template"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* New Template Tab */}
        {activeTab === 'new' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Create New Palliative Care Template</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Template Name</label>
                  <input
                    type="text"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    placeholder="Enter template name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500">
                    <option value="">Select category...</option>
                    <option value="Comfort Care">Comfort Care</option>
                    <option value="Symptom Management">Symptom Management</option>
                    <option value="Psychosocial Care">Psychosocial Care</option>
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    rows={3}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    placeholder="Enter template description..."
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Template Components</label>
                  <div className="space-y-2">
                    {['Pain Management', 'Symptom Control', 'Family Support', 'Spiritual Care', 'Advance Directives'].map((component, index) => (
                      <label key={index} className="flex items-center">
                        <input type="checkbox" className="mr-2" />
                        <span className="text-sm text-gray-700">{component}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setActiveTab('templates')}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    showNotification('Palliative care template created successfully', 'success');
                    setActiveTab('templates');
                  }}
                  className="px-6 py-3 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                >
                  Create Template
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Template Detail Modal */}
        {selectedTemplate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
            <div className="bg-white rounded-2xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-800">Template Details: {selectedTemplate.name}</h3>
                <button
                  onClick={() => setSelectedTemplate(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Basic Information</h4>
                    <div className="space-y-2 text-sm">
                      <p><strong>Name:</strong> {selectedTemplate.name}</p>
                      <p><strong>Category:</strong> {selectedTemplate.category}</p>
                      <p><strong>Status:</strong> <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(selectedTemplate.status)}`}>{selectedTemplate.status}</span></p>
                      <p><strong>Created By:</strong> {selectedTemplate.createdBy}</p>
                      <p><strong>Created Date:</strong> {selectedTemplate.createdDate}</p>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Usage Statistics</h4>
                    <div className="space-y-2 text-sm">
                      <p><strong>Usage Count:</strong> {selectedTemplate.usageCount}</p>
                      <p><strong>Last Used:</strong> {selectedTemplate.lastUsed}</p>
                      <p><strong>Active Residents:</strong> {selectedTemplate.residents.length}</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Description</h4>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">{selectedTemplate.description}</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Template Components</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {selectedTemplate.components.map((component, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-sm text-gray-700">{component}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Active Residents</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedTemplate.residents.map((resident, index) => (
                      <span key={index} className="px-3 py-1 bg-pink-100 text-pink-800 text-sm rounded-full">
                        {resident}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setSelectedTemplate(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => handleEditTemplate(selectedTemplate)}
                  className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                >
                  Edit Template
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PalliativeCareTemplates;
